import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ProfessionalType, PROFESSIONAL_PROFILES, canAccessModule } from '@/types/professional';
import { AVAILABLE_MODULES, ModuleConfig } from '@/types/modules';

interface ProfessionalStore {
  // État
  professionalType: ProfessionalType;
  hasSelectedProfile: boolean; // L'utilisateur a-t-il fait un choix initial?

  // Actions
  setProfessionalType: (type: ProfessionalType) => void;
  setInitialProfile: (type: ProfessionalType) => void; // Sélection initiale
  resetProfileSelection: () => void; // Réinitialiser pour forcer une nouvelle sélection

  // Getters filtrés
  getFilteredModules: () => ModuleConfig[];
  canAccessModule: (moduleId: string) => boolean;
  isSimplifiedMode: () => boolean;
}

export const useProfessionalStore = create<ProfessionalStore>()(
  persist(
    (set, get) => ({
      professionalType: 'INF', // Par défaut infirmière
      hasSelectedProfile: false, // Pas encore de sélection initiale

      setProfessionalType: (type) => set({ professionalType: type }),

      setInitialProfile: (type) =>
        set({
          professionalType: type,
          hasSelectedProfile: true,
        }),

      resetProfileSelection: () => set({ hasSelectedProfile: false }),

      getFilteredModules: () => {
        const { professionalType } = get();
        const profile = PROFESSIONAL_PROFILES[professionalType];

        if (profile.allowedModules.includes('*')) {
          return AVAILABLE_MODULES.filter((m) => !m.hidden);
        }

        return AVAILABLE_MODULES.filter((m) => !m.hidden && profile.allowedModules.includes(m.id));
      },

      // Visit profiles archived - getFilteredProfiles removed
      // canAccessProfile removed

      canAccessModule: (moduleId) => {
        return canAccessModule(get().professionalType, moduleId);
      },

      isSimplifiedMode: () => {
        return get().professionalType === 'INF_AUX';
      },
    }),
    {
      name: 'clara-professional',
      storage: createJSONStorage(() => localStorage),
      version: 2, // v2: Visit profiles archived (getFilteredProfiles, canAccessProfile removed)
      migrate: (persistedState, version) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const state = persistedState as any;
        if (version < 2) {
          // Ensure clean state from older versions
          // Keep only professionalType and hasSelectedProfile
          return {
            professionalType: state.professionalType || 'INF',
            hasSelectedProfile: state.hasSelectedProfile || false,
          };
        }
        return state;
      },
    }
  )
);
